import { useState } from 'react';
import type { JSX } from 'react';

import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { useCreateCredentialMutation, useUpdateCredentialMutation } from '../api/integrations';
import { formatJson, parseJsonObject } from '../lib/json';
import { PROVIDER_CATALOG } from '../model/integration';
import type { IntegrationCredential } from '../model/integration';
import FormDialog, { Field } from './FormDialog';

interface CredentialFormDialogProps {
  provider: string;
  /** Null creates a new one */
  credential: IntegrationCredential | null;
  onClose: () => void;
}

const CredentialFormDialog = ({ provider, credential, onClose }: CredentialFormDialogProps): JSX.Element => {
  const example = PROVIDER_CATALOG[provider];
  const [label, setLabel] = useState(credential?.label ?? '');
  const [settings, setSettings] = useState(formatJson(credential?.settings ?? example?.settingsExample ?? {}));
  // Secrets never come back from the API, so editing starts empty and an empty box leaves them as they are
  const [secrets, setSecrets] = useState(credential ? '' : formatJson(example?.secretsExample ?? {}));
  const [error, setError] = useState<string | null>(null);

  const [createCredential, { isLoading: isCreating }] = useCreateCredentialMutation();
  const [updateCredential, { isLoading: isUpdating }] = useUpdateCredentialMutation();

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    const parsedSettings = parseJsonObject(settings);
    if (parsedSettings.error !== null) {
      setError(`Settings: ${parsedSettings.error}`);
      return;
    }
    const parsedSecrets = secrets.trim() ? parseJsonObject(secrets) : null;
    if (parsedSecrets && parsedSecrets.error !== null) {
      setError(`Secrets: ${parsedSecrets.error}`);
      return;
    }
    try {
      if (credential) {
        await updateCredential({
          id: credential.id,
          provider,
          body: { settings: parsedSettings.value, ...(parsedSecrets && { secrets: parsedSecrets.value }) },
        }).unwrap();
      } else {
        await createCredential({
          provider,
          label: label.trim(),
          settings: parsedSettings.value,
          secrets: parsedSecrets?.value ?? {},
        }).unwrap();
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo guardar la credencial'));
    }
  };

  const setSecretKeys = Object.keys(credential?.secrets ?? {});

  return (
    <FormDialog
      title={credential ? `Editar credencial #${credential.id} · ${credential.label}` : `Nueva credencial ${provider}`}
      description={
        credential
          ? 'Una credencial puede estar compartida entre varias integraciones: el cambio aplica a todas.'
          : undefined
      }
      submitLabel={credential ? 'Guardar' : 'Crear'}
      isSaving={isCreating || isUpdating}
      error={error}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      {!credential ? (
        <Field label="Nombre" htmlFor="credential-label" hint="No se puede cambiar después.">
          <Input id="credential-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
        </Field>
      ) : null}
      <Field
        label="Settings (JSON)"
        htmlFor="credential-settings"
        hint={
          <>
            Cómo conectarse: URLs, formatos. Se reemplaza completo.
            {example?.credentialHint ? ` ${example.credentialHint}` : null}
          </>
        }
      >
        <Textarea
          id="credential-settings"
          value={settings}
          onChange={(e) => setSettings(e.target.value)}
          rows={5}
          className="font-mono text-xs"
        />
      </Field>
      <Field
        label="Secrets (JSON)"
        htmlFor="credential-secrets"
        hint={
          credential ? (
            <>
              Vacío = no cambia nada. Solo se reemplazan las secciones de primer nivel que envíes. Definidos hoy:{' '}
              {setSecretKeys.length > 0 ? setSecretKeys.join(', ') : 'ninguno'}.
            </>
          ) : (
            'Nunca se vuelven a mostrar después de guardarlos.'
          )
        }
      >
        <Textarea
          id="credential-secrets"
          value={secrets}
          onChange={(e) => setSecrets(e.target.value)}
          rows={5}
          className="font-mono text-xs"
          placeholder={credential ? formatJson(example?.secretsExample ?? {}) : undefined}
          autoComplete="off"
          spellCheck={false}
        />
      </Field>
    </FormDialog>
  );
};

export default CredentialFormDialog;
